import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { makeT } from "@/lib/labI18n";
import { useLabAuth } from "@/pages/Lab/LabAuth";
import { defaultProject, defaultTeam, defaultEnergy, migrateEnergy, defaultSignalZone, seedSignalZones, newWifiEntry, newMobileEntry, newId } from "@/lib/labData";
import { fetchWorkspace, saveProject, deleteProjectApi, saveReports, migrateLocal, fetchActivity } from "@/lib/labApi";

const STORAGE_KEY = "nexis_lab_v01";
const DEMO_ID = "proj-hotel-01";
const LabContext = createContext(null);

function freshState() {
  const proj = defaultProject();
  return {
    language: "pt",
    projects: [proj],
    activeProjectId: proj.id,
    reports: [],
    team: defaultTeam(),
  };
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return freshState();
    const parsed = JSON.parse(raw);
    if (!parsed.projects || !Array.isArray(parsed.projects)) return freshState();
    parsed.projects = parsed.projects.map((p) => {
      const baseZones = (p.id === DEMO_ID && (!p.signalZones || p.signalZones.length === 0)) ? seedSignalZones() : (p.signalZones || []);
      return { ...p, energy: migrateEnergy(p.energy), signalZones: baseZones.map((z) => ({ building: "", plan: null, ...z })), coverageMap: p.coverageMap ?? null, blueprint: p.blueprint || { pages: [] }, points: p.points || [] };
    });
    return { ...freshState(), ...parsed };
  } catch {
    return freshState();
  }
}

export function LabProvider({ children }) {
  const auth = useLabAuth();
  const user = auth && typeof auth.user === "object" ? auth.user : null;
  const [state, setState] = useState(load);
  const [sync, setSync] = useState({ status: "connecting", migration: false, lastSync: null });

  // keep latest state for async closures without stale captures
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  // sync bookkeeping
  const versions = useRef({});       // projectId -> server version
  const lastPushed = useRef({});     // projectId -> JSON snapshot last confirmed on server
  const pendingAction = useRef({});  // projectId -> { action, detail }
  const lastReports = useRef(null);  // JSON snapshot of reports last pushed
  const knownIds = useRef(new Set());
  const bootstrapped = useRef(false);

  // local cache / offline backup — always keep a copy on this device
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
  }, [state]);

  // ---- bootstrap: pull the shared team workspace from the backend ----------
  useEffect(() => {
    if (!user || bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      try {
        const ws = await fetchWorkspace();
        versions.current = ws.versions || {};
        knownIds.current = new Set(Object.keys(versions.current));
        if (ws.projects && ws.projects.length) {
          ws.projects.forEach((p) => { lastPushed.current[p.id] = JSON.stringify(p); });
          lastReports.current = JSON.stringify(ws.reports || []);
          setState((s) => {
            const activeOk = ws.projects.some((p) => p.id === s.activeProjectId);
            return {
              ...s,
              projects: ws.projects,
              reports: (ws.reports && ws.reports.length) ? ws.reports : s.reports,
              activeProjectId: activeOk ? s.activeProjectId : ws.projects[0].id,
            };
          });
          setSync({ status: "synced", migration: false, lastSync: new Date().toISOString() });
        } else {
          // server empty → seed the shared demo, or offer migration for real local data
          const local = stateRef.current;
          const extra = local.projects.filter((p) => p.id !== DEMO_ID);
          if (extra.length > 0) {
            setSync({ status: "synced", migration: true, lastSync: null });
          } else {
            for (const p of local.projects) {
              try {
                const res = await saveProject(p.id, p, undefined, "Criou o projeto");
                versions.current[p.id] = res.version;
                lastPushed.current[p.id] = JSON.stringify(p);
                knownIds.current.add(p.id);
              } catch { /* keep going */ }
            }
            lastReports.current = JSON.stringify(local.reports || []);
            setSync({ status: "synced", migration: false, lastSync: new Date().toISOString() });
          }
        }
      } catch {
        setSync({ status: "offline", migration: false, lastSync: null });
      }
    })();
  }, [user]);

  // ---- push dirty projects (debounced) -------------------------------------
  const pushDirty = useRef(async () => {});
  pushDirty.current = async () => {
    const projs = stateRef.current.projects;
    // deletions
    for (const id of Array.from(knownIds.current)) {
      if (!projs.some((p) => p.id === id)) {
        try { await deleteProjectApi(id); } catch { /* ignore */ }
        knownIds.current.delete(id);
        delete versions.current[id];
        delete lastPushed.current[id];
      }
    }
    for (const p of projs) {
      const snap = JSON.stringify(p);
      if (lastPushed.current[p.id] === snap) continue;
      const pa = pendingAction.current[p.id] || {};
      try {
        const res = await saveProject(p.id, p, versions.current[p.id], pa.action, pa.detail);
        versions.current[p.id] = res.version;
        lastPushed.current[p.id] = snap;
        knownIds.current.add(p.id);
        delete pendingAction.current[p.id];
        setSync((x) => ({ ...x, status: "synced", lastSync: new Date().toISOString() }));
      } catch (e) {
        if (e.conflict) {
          const srv = e.conflict;
          versions.current[p.id] = srv.version;
          lastPushed.current[p.id] = JSON.stringify(srv.data);
          setState((s) => ({ ...s, projects: s.projects.map((x) => (x.id === p.id ? srv.data : x)) }));
          toast.warning(`Projeto atualizado por ${srv.updated_by_name || "outro utilizador"}. A versão mais recente foi carregada.`);
        } else {
          setSync((x) => ({ ...x, status: "offline" }));
        }
      }
    }
  };

  useEffect(() => {
    if (!bootstrapped.current) return;
    if (sync.status === "connecting" || sync.migration) return;
    const h = setTimeout(() => { pushDirty.current(); }, 900);
    return () => clearTimeout(h);
  }, [state.projects, sync.status, sync.migration]);

  // ---- push reports (debounced) --------------------------------------------
  useEffect(() => {
    if (!bootstrapped.current || sync.status === "connecting" || sync.migration) return;
    const snap = JSON.stringify(state.reports || []);
    if (lastReports.current === null || lastReports.current === snap) return;
    const h = setTimeout(() => {
      saveReports(stateRef.current.reports || []).then(() => { lastReports.current = snap; }).catch(() => {});
    }, 900);
    return () => clearTimeout(h);
  }, [state.reports, sync.status, sync.migration]);

  const t = useMemo(() => makeT(state.language), [state.language]);

  const api = useMemo(() => {
    const author = user ? { id: user.id, name: user.name } : null;
    const nowIso = () => new Date().toISOString();
    const stampAuthor = (obj) => ({ author, authorName: author ? author.name : "", ...obj });
    const note = (projectId, action, detail = "") => { pendingAction.current[projectId] = { action, detail }; };

    const setProjects = (updater) =>
      setState((s) => ({ ...s, projects: typeof updater === "function" ? updater(s.projects) : updater }));

    const updateProject = (id, patch) =>
      setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, ...(typeof patch === "function" ? patch(p) : patch) } : p)));

    return {
      t,
      currentUser: user,
      syncStatus: sync.status,
      migrationAvailable: sync.migration,
      lastSync: sync.lastSync,
      setLanguage: (language) => setState((s) => ({ ...s, language })),
      setActiveProject: (id) => setState((s) => ({ ...s, activeProjectId: id })),

      createProject: ({ name, category, location, zones }) => {
        const p = {
          id: newId("proj"),
          name, category, status: "simulated", location,
          zones: zones || [], engineers: [], notes: "",
          createdAt: nowIso(), createdBy: author,
          topology: { nodes: [], edges: [] },
          measurements: [], experiments: [], signalZones: [],
          coverageMap: null, blueprint: { pages: [] }, points: [],
        };
        note(p.id, "Criou o projeto", name);
        setState((s) => ({ ...s, projects: [...s.projects, p], activeProjectId: p.id }));
        return p;
      },

      deleteProject: (id) =>
        setState((s) => {
          const projects = s.projects.filter((p) => p.id !== id);
          const activeProjectId = s.activeProjectId === id ? projects[0]?.id ?? null : s.activeProjectId;
          const reports = s.reports.filter((r) => r.projectId !== id);
          return { ...s, projects, activeProjectId, reports };
        }),

      updateProject,

      addMeasurement: (projectId, m) => {
        note(projectId, "Adicionou medição");
        updateProject(projectId, (p) => ({ measurements: [stampAuthor({ id: newId("m"), at: nowIso(), ...m }), ...(p.measurements || [])] }));
      },

      deleteMeasurement: (projectId, mid) => {
        note(projectId, "Removeu medição");
        updateProject(projectId, (p) => ({ measurements: (p.measurements || []).filter((m) => m.id !== mid) }));
      },

      toggleMeasurementVerified: (projectId, mid) =>
        updateProject(projectId, (p) => ({
          measurements: (p.measurements || []).map((m) => (m.id === mid ? { ...m, verified: !m.verified } : m)),
        })),

      // --- Signal Zones ---
      addSignalZone: (projectId, name = "") => {
        const z = stampAuthor(defaultSignalZone(name));
        note(projectId, "Adicionou zona de sinal", name);
        updateProject(projectId, (p) => ({ signalZones: [...(p.signalZones || []), z] }));
        return z.id;
      },
      updateSignalZone: (projectId, zoneId, patch) => {
        note(projectId, "Editou zona de sinal");
        updateProject(projectId, (p) => ({ signalZones: (p.signalZones || []).map((z) => (z.id === zoneId ? { ...z, ...patch } : z)) }));
      },
      removeSignalZone: (projectId, zoneId) => {
        note(projectId, "Removeu zona de sinal");
        updateProject(projectId, (p) => ({ signalZones: (p.signalZones || []).filter((z) => z.id !== zoneId) }));
      },
      duplicateSignalZone: (projectId, zoneId) =>
        updateProject(projectId, (p) => {
          const zones = p.signalZones || [];
          const idx = zones.findIndex((z) => z.id === zoneId);
          if (idx < 0) return {};
          const src = zones[idx];
          const copy = {
            ...src, id: newId("zone"), name: `${src.name} (cópia)`, status: "pending",
            wifi: (src.wifi || []).map((w) => ({ ...w, id: newId("wifi") })),
            mobile: (src.mobile || []).map((m) => ({ ...m, id: newId("mob") })),
            photos: (src.photos || []).map((ph) => ({ ...ph, id: newId("zph") })),
          };
          const next = [...zones];
          next.splice(idx + 1, 0, copy);
          return { signalZones: next };
        }),
      moveSignalZone: (projectId, zoneId, dir) =>
        updateProject(projectId, (p) => {
          const zones = [...(p.signalZones || [])];
          const i = zones.findIndex((z) => z.id === zoneId);
          const j = i + dir;
          if (i < 0 || j < 0 || j >= zones.length) return {};
          [zones[i], zones[j]] = [zones[j], zones[i]];
          return { signalZones: zones };
        }),
      toggleArchiveZone: (projectId, zoneId) =>
        updateProject(projectId, (p) => ({ signalZones: (p.signalZones || []).map((z) => (z.id === zoneId ? { ...z, archived: !z.archived } : z)) })),
      addZoneMeas: (projectId, zoneId, kind, entry) => {
        note(projectId, kind === "wifi" ? "Adicionou medição Wi-Fi" : "Adicionou medição móvel");
        updateProject(projectId, (p) => ({
          signalZones: (p.signalZones || []).map((z) => {
            if (z.id !== zoneId) return z;
            const base = kind === "wifi" ? newWifiEntry() : newMobileEntry();
            return { ...z, [kind]: [stampAuthor({ ...base, ...entry }), ...(z[kind] || [])] };
          }),
        }));
      },
      updateZoneMeas: (projectId, zoneId, kind, mid, patch) =>
        updateProject(projectId, (p) => ({
          signalZones: (p.signalZones || []).map((z) => (z.id !== zoneId ? z : { ...z, [kind]: (z[kind] || []).map((m) => (m.id === mid ? { ...m, ...patch } : m)) })),
        })),
      removeZoneMeas: (projectId, zoneId, kind, mid) =>
        updateProject(projectId, (p) => ({
          signalZones: (p.signalZones || []).map((z) => (z.id !== zoneId ? z : { ...z, [kind]: (z[kind] || []).filter((m) => m.id !== mid) })),
        })),
      addZonePhotos: (projectId, zoneId, photos) => {
        note(projectId, `Adicionou ${photos.length} fotografia(s) à zona`);
        const stamped = photos.map((ph) => stampAuthor({ ...ph }));
        updateProject(projectId, (p) => ({
          signalZones: (p.signalZones || []).map((z) => (z.id !== zoneId ? z : { ...z, photos: [...(z.photos || []), ...stamped] })),
        }));
      },
      removeZonePhoto: (projectId, zoneId, pid) =>
        updateProject(projectId, (p) => ({
          signalZones: (p.signalZones || []).map((z) => (z.id !== zoneId ? z : { ...z, photos: (z.photos || []).filter((ph) => ph.id !== pid) })),
        })),
      setZonePhotoCaption: (projectId, zoneId, pid, caption) =>
        updateProject(projectId, (p) => ({
          signalZones: (p.signalZones || []).map((z) => (z.id !== zoneId ? z : { ...z, photos: (z.photos || []).map((ph) => (ph.id === pid ? { ...ph, caption } : ph)) })),
        })),
      setCoverageMap: (projectId, coverageMap) => updateProject(projectId, { coverageMap }),

      // --- Blueprint (Planta & Cobertura) ---
      addBlueprintPages: (projectId, pages) => {
        note(projectId, "Adicionou planta");
        updateProject(projectId, (p) => ({ blueprint: { pages: [...((p.blueprint && p.blueprint.pages) || []), ...pages] } }));
      },
      removeBlueprintPage: (projectId, pageId) =>
        updateProject(projectId, (p) => ({
          blueprint: { pages: ((p.blueprint && p.blueprint.pages) || []).filter((pg) => pg.id !== pageId) },
          points: (p.points || []).filter((pt) => pt.pageId !== pageId),
        })),
      renameBlueprintPage: (projectId, pageId, name) =>
        updateProject(projectId, (p) => ({ blueprint: { pages: ((p.blueprint && p.blueprint.pages) || []).map((pg) => (pg.id === pageId ? { ...pg, name } : pg)) } })),
      setZonePlanPos: (projectId, zoneId, plan) =>
        updateProject(projectId, (p) => ({ signalZones: (p.signalZones || []).map((z) => (z.id === zoneId ? { ...z, plan } : z)) })),
      addPoint: (projectId, point) =>
        updateProject(projectId, (p) => ({ points: [...(p.points || []), stampAuthor(point)] })),
      updatePoint: (projectId, pointId, patch) =>
        updateProject(projectId, (p) => ({ points: (p.points || []).map((pt) => (pt.id === pointId ? { ...pt, ...patch } : pt)) })),
      removePoint: (projectId, pointId) =>
        updateProject(projectId, (p) => ({ points: (p.points || []).filter((pt) => pt.id !== pointId) })),

      saveTopology: (projectId, topology) => { note(projectId, "Guardou topologia"); updateProject(projectId, { topology }); },

      addExperiment: (projectId, exp) => {
        note(projectId, "Registou experiência");
        updateProject(projectId, (p) => ({ experiments: [stampAuthor({ id: newId("exp"), at: nowIso(), ...exp }), ...(p.experiments || [])] }));
      },

      addReport: (report) => {
        const r = { id: newId("rep"), createdAt: nowIso(), author, authorName: author ? author.name : "", ...report };
        setState((s) => ({ ...s, reports: [r, ...s.reports] }));
        return r;
      },

      // --- HX Stable Energy ---
      setEnergyAssessment: (projectId, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, assessment: { ...e.assessment, ...patch } } };
        }),
      setChecklistItem: (projectId, itemId, status) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          const next = { ...e.checklist };
          if (next[itemId] === status) delete next[itemId];
          else next[itemId] = status;
          return { energy: { ...e, checklist: next } };
        }),
      addEnergyPhotos: (projectId, photos) => {
        note(projectId, `Adicionou ${photos.length} fotografia(s) de energia`);
        const stamped = photos.map((ph) => stampAuthor({ ...ph }));
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, photos: [...e.photos, ...stamped] } };
        });
      },
      removeEnergyPhoto: (projectId, pid) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, photos: e.photos.filter((ph) => ph.id !== pid) } };
        }),
      setSurvey: (projectId, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, survey: { ...(e.survey || {}), ...patch } } };
        }),
      setClient: (projectId, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, client: { ...(e.client || {}), ...patch } } };
        }),
      setCheckDetail: (projectId, itemId, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          const cd = e.checkDetails || {};
          return { energy: { ...e, checkDetails: { ...cd, [itemId]: { ...(cd[itemId] || {}), ...patch } } } };
        }),
      setAuthStatus: (projectId, authStatus) =>
        updateProject(projectId, (p) => ({ energy: { ...(p.energy || defaultEnergy()), authStatus } })),
      setFieldMeta: (projectId, key, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          const fm = e.fieldMeta || {};
          return { energy: { ...e, fieldMeta: { ...fm, [key]: { ...(fm[key] || {}), ...patch } } } };
        }),
      addGenerator: (projectId) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          const g = { id: newId("gen"), maker: "", model: "", powerKva: "", fuel: "", consumption: "", hours: "", condition: "", switchgear: "", autoManual: "" };
          return { energy: { ...e, generators: [...(e.generators || []), g] } };
        }),
      updateGenerator: (projectId, id, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, generators: (e.generators || []).map((g) => (g.id === id ? { ...g, ...patch } : g)) } };
        }),
      removeGenerator: (projectId, id) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, generators: (e.generators || []).filter((g) => g.id !== id) } };
        }),
      addCriticalLoad: (projectId) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          const c = { id: newId("cl"), name: "", qty: "", power: "", hours: "", priority: "", backup: false, simultaneous: false, notes: "" };
          return { energy: { ...e, criticalLoads: [...(e.criticalLoads || []), c] } };
        }),
      updateCriticalLoad: (projectId, id, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, criticalLoads: (e.criticalLoads || []).map((c) => (c.id === id ? { ...c, ...patch } : c)) } };
        }),
      removeCriticalLoad: (projectId, id) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, criticalLoads: (e.criticalLoads || []).filter((c) => c.id !== id) } };
        }),
      addLoadReading: (projectId, reading) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          const r = { id: newId("rd"), datetime: "", value: "", origin: "", ...reading };
          return { energy: { ...e, loadReadings: [...(e.loadReadings || []), r] } };
        }),
      removeLoadReading: (projectId, id) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, loadReadings: (e.loadReadings || []).filter((r) => r.id !== id) } };
        }),
      updateLoadReading: (projectId, id, patch) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, loadReadings: (e.loadReadings || []).map((r) => (r.id === id ? { ...r, ...patch } : r)) } };
        }),
      addLoadFiles: (projectId, files) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, loadFiles: [...(e.loadFiles || []), ...files] } };
        }),
      removeLoadFile: (projectId, id) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, loadFiles: (e.loadFiles || []).filter((f) => f.id !== id) } };
        }),
      setPhotoCategory: (projectId, pid, category) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, photos: e.photos.map((ph) => (ph.id === pid ? { ...ph, category } : ph)) } };
        }),
      setPhotoCaption: (projectId, pid, caption) =>
        updateProject(projectId, (p) => {
          const e = p.energy || defaultEnergy();
          return { energy: { ...e, photos: e.photos.map((ph) => (ph.id === pid ? { ...ph, caption } : ph)) } };
        }),
      replaceProject: (proj) =>
        setState((s) => {
          const p = { ...proj, energy: migrateEnergy(proj.energy) };
          const exists = s.projects.some((x) => x.id === p.id);
          const projects = exists ? s.projects.map((x) => (x.id === p.id ? p : x)) : [...s.projects, p];
          return { ...s, projects, activeProjectId: p.id };
        }),

      resetDemo: () => setState(freshState()),

      // --- shared workspace helpers ---
      exportBackup: () => {
        try {
          const blob = new Blob([JSON.stringify(stateRef.current, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `nexis-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        } catch { /* ignore */ }
      },

      migrateLocalToServer: async () => {
        const local = stateRef.current;
        const res = await migrateLocal(local.projects, local.reports || []);
        const ws = await fetchWorkspace();
        versions.current = ws.versions || {};
        knownIds.current = new Set(Object.keys(versions.current));
        (ws.projects || []).forEach((p) => { lastPushed.current[p.id] = JSON.stringify(p); });
        lastReports.current = JSON.stringify(ws.reports || []);
        setState((s) => ({
          ...s,
          projects: ws.projects || s.projects,
          reports: (ws.reports && ws.reports.length) ? ws.reports : s.reports,
          activeProjectId: (ws.projects || []).some((p) => p.id === s.activeProjectId) ? s.activeProjectId : ((ws.projects || [])[0]?.id ?? s.activeProjectId),
        }));
        setSync({ status: "synced", migration: false, lastSync: new Date().toISOString() });
        return res;
      },

      fetchActivity,
    };
  }, [t, user, sync.status, sync.migration, sync.lastSync]);

  const value = useMemo(() => {
    const activeProject = state.projects.find((p) => p.id === state.activeProjectId) || state.projects[0] || null;
    return { ...state, activeProject, ...api };
  }, [state, api]);

  return <LabContext.Provider value={value}>{children}</LabContext.Provider>;
}

export function useLab() {
  const ctx = useContext(LabContext);
  if (!ctx) throw new Error("useLab must be used within LabProvider");
  return ctx;
}
